// @flow

import React, { useState, useCallback } from 'react'
import FlipMove from 'react-flip-move'
import { debounce, get } from 'lodash'
import { TextInput, LoadingSpinner } from '@paypalcorp/pp-react'
import Availability from './availability'
import { handleAvailabilityAndSuggestions } from '../../../gql/mutation/handle-availability-and-suggestions'
import clientData from 'utils/client-data'

import getContent from 'pp-react-l10n'
const l10n = getContent('pages/seller/details')

import axios from 'axios'

const { CancelToken } = axios

const helperText = l10n('main.businessHandleInput.helper')

// position the loading spinner within the text input
const styleSpinnerPosition = {
  marginRight: '0.75rem',
  marginLeft: '0.75rem',
  height: '1.5rem',
  width: '1.5rem',
}

const errorMessage = {
  invalid: l10n('main.businessHandleInput.error.invalid'),
  tooShort: l10n('main.businessHandleInput.error.beOver'),
  tooLong: l10n('main.businessHandleInput.error.beUnder'),
  taken: l10n('main.businessHandleInput.error.taken'),
}

let cancelToken

function UserHandleInput(props) {
  const {
    sellerProfileState: {
      state: { businessName = '' } = {},
      updateBusinessDetails = () => {},
    } = {},
    handleChangeInput = () => {},
  } = props

  const delayedValidation = useCallback(
    debounce((fn, ...args) => fn(...args), 500),
    [],
  )

  // hooks
  const [isHelperTextVisible, setIsHelperTextVisible] = useState(false)
  const [
    isCheckingHandleAvailability,
    setIsCheckingHandleAvailability,
  ] = useState(false)
  const [isHandleAvailable, setIsHandleAvailable] = useState(false)
  const [suggestions, setSuggestions] = useState([])
  const [userInput, setUserInput] = useState('')
  const [error, setError] = useState({
    type: 'unknown',
    isError: false,
  })

  // this text is here instead of above becuase it relies on the userInput
  const availableText = l10n('main.businessHandleInput.available', {
    handle: userInput,
  })

  // when a user makes a selection based on suggestions
  const onSelectSuggestion = e => {
    const selectedSuggestion = e.target.value
    setIsHandleAvailable(true)
    setError({ isError: false })
    setUserInput(selectedSuggestion)
    handleChangeInput(e)
    updateBusinessDetails({
      isValidBusinessHandle: true,
      isSuggestedBusinessHandle: true,
    })
  }

  // when a user types into input box
  const onChange = async e => {
    handleChangeInput(e)
    setUserInput(e.target.value)
    updateBusinessDetails({
      businessHandle: e.target.value,
      isValidBusinessHandle: false,
      isSuggestedBusinessHandle: false,
    })
    // on tab out validate right away
    setIsHandleAvailable(false)
    if (e.type === 'blur') {
      validateUserInput(e.target.value)
    } else {
      // when typing validate with a delay
      await delayedValidation(validateUserInput, e.target.value)
    }
  }

  const validateUserInput = value => {
    if (value.length === 0) {
      setError({ type: 'unknown', isError: false })
      setIsHelperTextVisible(true)
    } else if (value.length < 3) {
      setError({ type: 'tooShort', isError: true })
    } else if (value.length > 20) {
      setError({ type: 'tooLong', isError: true })
    } else if (!validateChar(value)) {
      setError({ type: 'invalid', isError: true })
    } else {
      // passed initial front end validation let's
      // send it over to the service to see if its
      // taken and get suggestsions if it is
      setError({
        type: 'unknown',
        isError: false,
      })
      checkHandleAvailability(value)
    }
  }

  const validateChar = (value: string) => {
    const SLUG_VALIDATION_REGEX = /^[a-z0-9]*$/i

    if (SLUG_VALIDATION_REGEX.test(value)) {
      return true
    }
    return false
  }

  const checkHandleAvailability = async value => {
    // load spinner into input field
    setIsCheckingHandleAvailability(true)

    //Check if there are any previous pending requests
    if (typeof cancelToken != typeof undefined) {
      cancelToken.cancel('Operation canceled due to new request.')
    }

    cancelToken = CancelToken.source()

    const variables = {
      handle: {
        preferred_id: value,
        business_name: businessName,
      },
    }

    const availabilityPayload = {
      query: handleAvailabilityAndSuggestions,
      variables,
    }
    const cancelPayload = {
      cancelToken: cancelToken.token,
    }
    const graphqlEndpoint = `${window.location.origin}${clientData.requestURI}/v1/graphql`

    let availabilityResponse
    axios.defaults.headers.post['x-csrf-token'] = get(clientData, 'csrf')

    try {
      availabilityResponse = await axios.post(
        graphqlEndpoint,
        availabilityPayload,
        cancelPayload,
      )
      setIsCheckingHandleAvailability(false)

      const isHandleAvailable = get(
        availabilityResponse,
        'data.data.handleAvailabilityAndSuggestions.isPreferredIdAvailable',
        false,
      )
      setIsHandleAvailable(isHandleAvailable)

      const suggestedSlugs = get(
        availabilityResponse,
        'data.data.handleAvailabilityAndSuggestions.suggestedSlugs',
        [],
      )
      setSuggestions(suggestedSlugs)

      if (!isHandleAvailable) {
        setError({
          type: 'taken',
          isError: true,
        })
      } else {
        updateBusinessDetails({
          isValidBusinessHandle: true,
        })
      }
    } catch (error) {
      // debug(error)
    }
  }

  const toggleHelperText = () => {
    setIsHelperTextVisible(!isHelperTextVisible)
  }

  return (
    <>
      <TextInput
        data-test-id="businessHandleInput"
        name="businessHandle"
        value={userInput}
        autoComplete="off"
        prefix="@"
        label={l10n('main.businessHandleInput.label')}
        onChange={onChange}
        onBlur={toggleHelperText}
        onFocus={toggleHelperText}
        helperText={isHelperTextVisible && helperText}
        errorText={error.isError && errorMessage[error.type]}
        successText={isHandleAvailable && availableText}
        rightIcon={
          isCheckingHandleAvailability && (
            <LoadingSpinner
              style={styleSpinnerPosition}
              screenReaderText="loading"
            />
          )
        }
      />
      <FlipMove
        enterAnimation={'fade'}
        leaveAnimation={'fade'}
        duration={150}
        easing="ease-out"
        style={{ marginTop: 24 }}
      >
        {error.type === 'taken' && (
          <Availability
            onSelect={onSelectSuggestion}
            suggestions={suggestions}
          />
        )}
      </FlipMove>
    </>
  )
}

export default UserHandleInput
